<template>
  <v-container>
    <v-layout row wrap>
      <v-flex xs12 md8 offset-md2>
        <h1 class="primary--text mb-4">New Data Entry</h1>
        <app-alert class="mb-3" type="error" :message="error" @dismissed="error = false" />

        <v-card>
          <v-divider></v-divider>
          <v-form ref="form" @submit.prevent="doCreateAid">
            <v-container class="px-4">
              <v-layout justify-space-between row wrap>
                <v-flex xs12 sm6>
                  <v-select
                    label="State"
                    v-model="aid.state_id"
                    hint="The State where this Intervention was done"
                    persistent-hint
                    :items="stateOptions"
                    :loading="isLoading"
                    :disabled="isLoading"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-autocomplete
                    label="Location"
                    v-model="aid.location_id"
                    hint="The location where this Intervention was done"
                    persistent-hint
                    :items="locationOptions"
                    :rules="[rules.isRequired]"
                    :loading="isLoading"
                    :disabled="isLoading || !aid.state_id"
                    no-data-text="No locations in selected state"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12>
                  <v-autocomplete
                    label="Event"
                    v-model="aid.event_id"
                    hint="The cause of the Intervention"
                    persistent-hint
                    :items="eventOptions"
                    :loading="isLoading"
                    :disabled="isLoading || !aid.location_id"
                    :rules="[rules.isRequired]"
                    no-data-text="No events in selected location"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-autocomplete
                    label="Intervention Sector"
                    v-model="aid.sector_id"
                    :items="sectorOptions"
                    :loading="isLoading"
                    :disabled="isLoading"
                    :rules="[rules.isRequired]"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-select
                    label="Intervention Category"
                    v-model="aid.category_id"
                    :items="aidCategories"
                    :loading="isLoading"
                    :disabled="isLoading || !aid.sector_id"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>

                <v-flex xs12>
                  <v-textarea
                    label="Intervention Description"
                    v-model="aid.description"
                    hint="A description of the Intervention (Ex. Trainings on WASH)"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    rows="2"
                    required
                  ></v-textarea>
                </v-flex>

                <!-- Start Date Picker -->
                <v-flex xs12 sm6>
                  <v-dialog
                    ref="startDateDialog"
                    v-model="startDateModal"
                    :return-value.sync="aid.timestamp"
                    persistent
                    lazy
                    full-width
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="aid.timestamp"
                        label="Start Date"
                        hint="The start date of the Intervention"
                        persistent-hint
                        :rules="[rules.isRequired]"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="aid.timestamp" scrollable>
                      <v-spacer></v-spacer>
                      <v-btn flat color="primary" @click="startDateModal = false">Cancel</v-btn>
                      <v-btn
                        flat
                        color="primary"
                        @click="$refs.startDateDialog.save(aid.timestamp)"
                      >OK</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-flex>

                <!-- End Date Picker -->
                <v-flex xs12 sm6>
                  <v-dialog
                    ref="endDateDialog"
                    v-model="endDateModal"
                    :return-value.sync="aid.timestamp_end"
                    persistent
                    lazy
                    full-width
                    width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="aid.timestamp_end"
                        label="End Date"
                        hint="The end date of the Intervention"
                        persistent-hint
                        :disabled="!aid.timestamp"
                        :rules="[rules.isRequired]"
                        readonly
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="aid.timestamp_end"
                      :allowed-dates="allowedDates"
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn flat color="primary" @click="endDateModal = false">Cancel</v-btn>
                      <v-btn
                        flat
                        color="primary"
                        @click="$refs.endDateDialog.save(aid.timestamp_end)"
                      >OK</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-autocomplete
                    label="Partner"
                    v-model="aid.agency_id"
                    hint="The implementing partner"
                    persistent-hint
                    :items="agencyOptions"
                    :loading="isLoading"
                    :disabled="isLoading"
                    :rules="[rules.isRequired]"
                    required
                  ></v-autocomplete>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field
                    label="Intervention Sponsor"
                    type="text"
                    v-model="aid.sponsor"
                    hint="Name of Interventon Sponsor"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field
                    label="Intervention Value (₦)"
                    type="number"
                    v-model="aid.value"
                    hint="Total cost of Intervention in Naira (₦)"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field
                    label="Beneficiary Count"
                    type="number"
                    v-model.number="aid.count"
                    hint="An estimation of total beneficiary count"
                    persistent-hint
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6>
                  <v-select
                    label="Post Distribution Monitoring"
                    v-model="aid.pdm"
                    hint="Post Distribution Monitoring"
                    persistent-hint
                    :items="pdmOptions"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>

              </v-layout>

              <v-layout class="mt-3 mb-2">
                <v-btn depressed color="accent" type="submit" :disabled="isSaving">
                  Create
                  <v-progress-circular
                    class="ml-2"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="isSaving"
                  ></v-progress-circular>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn depressed @click="$router.go(-1)">Cancel</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

import { AID_CREATE } from '@/store/actions/aid'
import { STATE_FETCH } from '@/store/actions/state'
import { AGENCY_FETCH } from '@/store/actions/agency'
import { FETCH_CATEGORY } from '@/store/actions/category'
import { SECTOR_FETCH } from '@/store/actions/sector'
import { EVENT_FETCH } from '@/store/actions/event'
import { LOCATION_FETCH } from '@/store/actions/location'

import { progressOptions, booleanOptions } from '@/data/options'

export default {
  name: 'CreateAid',
  created () {
    this.isLoading = true
    this.aid.agent_id = this.currentUser._id // set agent id

    Promise.all([
      this.$store.dispatch(STATE_FETCH),
      this.$store.dispatch(AGENCY_FETCH),
      this.$store.dispatch(FETCH_CATEGORY, { model: 'category' }),
      this.$store.dispatch(SECTOR_FETCH),
      this.$store.dispatch(EVENT_FETCH),
      this.$store.dispatch(LOCATION_FETCH)
    ]).then(() => {
      this.isLoading = false
    })
  },
  data () {
    return {
      isSaving: false,
      isLoading: false,
      statusOptions: progressOptions,
      startDateModal: false,
      endDateModal: false,
      pdmOptions: booleanOptions,
      aid: {
        timestamp: '',
        timestamp_end: '',
        value: '',
        count: '',
        description: '',
        tag: '-',
        status: 'completed', // ongoing
        sponsor: '',
        pdm: '',
        event_id: '',
        state_id: '',
        agency_id: '',
        category_id: '',
        location_id: '',
        country_id: this.$settings.country,
        agent_id: ''
      },
      error: false,
      rules: {
        isRequired: value => this.$validator.isRequired(value)
      }
    }
  },
  computed: {
    sectorOptions () {
      return this.$store.getters.sectorsAsOptions
    },
    aidCategories () {
      return this.$store.getters.categoriesAsOptions('aid', this.aid.sector_id)
    },
    stateOptions () {
      let stateOptions = this.$store.getters.statesAsOptions
      // If state agent, show only agents state in list
      if (this.currentUser.parent && this.currentUser.parent === 'state') {
        const state = stateOptions.find(stateOption => this.currentUser.parent_id === stateOption.value)
        stateOptions = [state]
      }
      return stateOptions
    },
    agencyOptions () {
      let agencyOptions = this.$store.getters.agenciesAsOptions
      // If agency agent, show only agents agency in list
      if (this.currentUser.parent && this.currentUser.parent === 'agency') {
        const agency = agencyOptions.find(agencyOption => this.currentUser.parent_id === agencyOption.value)
        agencyOptions = [agency]
      }
      return agencyOptions
    },
    eventOptions () {
      return this.$store.getters.eventsAsOptions('location', this.aid.location_id)
    },
    locationOptions () {
      return this.$store.getters.locationsAsOptions(this.aid.state_id)
    },
    ...mapGetters({
      currentUser: 'currentUser'
    })
  },
  methods: {
    allowedDates (date) {
      return new Date(date) >= new Date(this.aid.timestamp)
    },
    doCreateAid () {
      if (!this.$refs.form.validate()) return

      this.isSaving = true
      this.error = false

      const newAid = Object.assign({}, this.aid)
      newAid.timestamp = Math.round((new Date(newAid.timestamp)).getTime() / 1000)
      newAid.timestamp_end = Math.round((new Date(newAid.timestamp_end)).getTime() / 1000)

      this.$store.dispatch(AID_CREATE, newAid)
        .then((response) => {
          this.$router.push('/aids/' + response.data + '?newAid=1')
        })
        .catch(error => (this.error = error.message))
        .finally(() => (this.isSaving = false))
    }
  }
}
</script>
